import { Model } from "@vuex-orm/core";
import type { Fields } from "@vuex-orm/core";
import type { Config } from "@vuex-orm/plugin-axios";
import { formatSecondsAsString } from "@modules/helpers";

import Video from "@models/Video";
import VoiceOver from "@models/VoiceOver";

export default class AudioRecording extends Model {
  // This is the name used as module name of the Vuex Store.
  public static entity = "audioRecordings";

  public static get apiConfig(): Config {
    return {
      // headers: { 'X-Requested-With': 'XMLHttpRequest' },
      baseURL: "/api/v1/audio-recordings",
    };
  }

  public static fields(): Fields {
    return {
      id: this.number(-1),
      filename: this.attr(null), // this.string(null) still coerces null to "null"
      contentType: this.attr(null), // this.string(null) still coerces null to "null"
      position: this.number(null).nullable(),
      duration: this.number(null).nullable(),
      // selected: this.boolean(), // possibly persist from getter? Do we even need that?
      url: this.string(null),
      audioFileKey: this.string(""),
      audioFileUrl: this.string(""),
      playlistUrl: this.string(null).nullable(),
      posterUrl: this.string(null).nullable(),
      videoId: this.number(null).nullable(),
      video: this.belongsTo(Video, "videoId"),
      voiceOverId: this.string(null),
      voiceOver: this.belongsTo(Video, "voiceOverId"),
      voiceKey: this.string(null).nullable(),
      text: this.string(null).nullable(), // for the speech to text
    };
  }

  public declare id: number;

  public declare filename: string | null;

  public declare contentType: string | null;

  public declare position: number | null;

  public declare duration: number | null;

  /**
   * possibly unused at the moment
   */
  public declare url: string | null;

  public declare audioFileKey: string;

  /**
   * can also be the video file if this is a visual voice over
   */
  public declare audioFileUrl: string;

  public declare playlistUrl: string | null;

  public declare posterUrl: string | null;

  public declare videoId: number | null;

  public declare video: Video;

  public declare voiceOverId: string | null;

  public declare voiceOver: Video;

  public declare voiceKey: string | null;

  public declare text: string | null;

  public async destroy(): Promise<void> {
    // Consideration: we may also want to do the server-update in the afterDelete hook, or now that
    // I think of it: I think we may have to give a bit of special care to Vuex ORM deletions and
    // how they relate to server-side deletes, anyways. Perhaps we want to use the soft-delete
    // plugin and then keep that same soft-deletion mark on the server.
    await this.$delete(); // This will trigger the afterDelete callback on the model.
    await AudioRecording.api().patch(this.id.toString(), {
      audioRecording: { voiceOverId: null }, // Hmm this is not entirely
    });
  }

  public get formattedDuration(): string {
    if (this.duration) {
      return formatSecondsAsString(this.duration);
    }
    return "";
  }

  public get selected(): boolean {
    if (!this.voiceOverId) return false;

    return this.id === VoiceOver.find(this.voiceOverId)?.selectedAudioRecordingId;
  }

  public get name(): string {
    return this.filename || `${this.isVideo ? "Video" : "Audio"} #${(this.position || 0) + 1}`;
  }

  public get isAudio(): boolean {
    return this.contentType?.startsWith("audio/") || false;
  }

  public get isVideo(): boolean {
    return this.contentType?.startsWith("video/") || false;
  }

  public get isPlayable(): boolean {
    return Boolean(this.audioFileUrl || this.playlistUrl);
  }
}
